import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading";
import { requiredValidation } from "@via/utils/form-validators";
import { type JSX, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useUpdateIntegrationToken } from "@via/api/integration-tokens.api";
import { useIntegrationTokens } from "@via/api/integration-tokens.api";
import FormInputComponent from "@via/components/inputs/form-input-component";
import SelectComponent from "@via/components/select/select";
import {
  getTokenTypeIcon,
  tokenTypeOptions,
} from "@via/pages/account/profile/integration-tokens/utils/helper";
import {
  CreateIntegrationTokenPayload,
  EditIntegrationTokenPayload,
  ServiceTokenListItem,
  ServiceTokenType,
} from "common";

type EditIntegrationTokenFormValues = CreateIntegrationTokenPayload & {
  /** Write only: left blank keeps the stored secret */
  replacementToken?: string;
};

export default function EditIntegrationTokenForm({
  setOpen,
  integrationToken,
}: {
  setOpen: (open: boolean) => void;
  integrationToken: ServiceTokenListItem | null;
}): JSX.Element {
  const handleClose = () => setOpen(false);
  const hasToken = integrationToken?.tokenType !== ServiceTokenType.Bitbucket;
  const isAnthropic =
    integrationToken?.tokenType === ServiceTokenType.Anthropic;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EditIntegrationTokenFormValues>({
    mode: "onChange",
    defaultValues: {
      replacementToken: "",
      tokenType: integrationToken!.tokenType,
      name: integrationToken!.name,
      credentialData: {
        token: hasToken
          ? (integrationToken!.credentialData as { token: string }).token
          : undefined,
        embeddingServiceTokenId: isAnthropic
          ? ((
              integrationToken!.credentialData as {
                embeddingServiceTokenId?: number | null;
              }
            ).embeddingServiceTokenId ?? null)
          : undefined,
      },
    },
  });

  const { data: embeddingTokensResponse, isLoading: isEmbeddingTokensLoading } =
    useIntegrationTokens(
      0,
      100,
      [],
      [
        {
          id: "tokenTypeIn",
          value: [ServiceTokenType.OpenAI, ServiceTokenType.Gemini],
        },
      ],
      false
    );

  const embeddingTokenOptions =
    embeddingTokensResponse?.data?.map(token => ({
      value: token.id,
      label: token.name,
      icon: getTokenTypeIcon(token.tokenType),
    })) ?? [];

  const {
    mutateAsync: updateIntegrationToken,
    isPending: isUpdateIntegrationTokenLoading,
    isSuccess: isUpdateIntegrationTokenSuccess,
  } = useUpdateIntegrationToken();

  const handleUpdateIntegrationToken = async (
    payload: EditIntegrationTokenFormValues
  ) => {
    const { name } = payload;
    const replacement = payload.replacementToken?.trim();
    const submission: EditIntegrationTokenPayload = {
      id: integrationToken!.id.toString(),
      name,
      ...(hasToken &&
        replacement && { credentialData: { token: replacement } }),
    };
    await updateIntegrationToken(submission);
  };

  useEffect(() => {
    isUpdateIntegrationTokenSuccess && handleClose();
  }, [isUpdateIntegrationTokenSuccess]);

  const onSubmit = handleSubmit(handleUpdateIntegrationToken);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Integration Token
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Update the integration token details.
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Token Type <span className="text-red-400">*</span>
                </label>
                <SelectComponent
                  control={control}
                  name="tokenType"
                  rules={requiredValidation("Token Type")}
                  options={tokenTypeOptions}
                  readOnly
                  formatOptionLabel={option => (
                    <div className="flex items-center gap-2">
                      <img
                        className="w-5 py-2"
                        alt={option.label}
                        src={option.icon}
                      />
                      <span>{option.label}</span>
                    </div>
                  )}
                  placeholder="Select Token Type"
                />
                {errors.tokenType && (
                  <span className="text-xs text-rose-500">
                    {errors.tokenType.message}
                  </span>
                )}
              </div>
              <FormInputComponent
                control={control}
                name="name"
                label="Token Name"
                placeholder="Enter Token Name"
                validations={requiredValidation("Token Name")}
                showRequiredField
              />
              {hasToken && (
                <>
                  <FormInputComponent
                    control={control}
                    name="credentialData.token"
                    label="Current Token"
                    disabled
                  />
                  <FormInputComponent
                    control={control}
                    name="replacementToken"
                    label="Replace Token"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Leave blank to keep the current token"
                    showTooltipIcon
                    tooltipText="Enter a new token to replace the stored one, for example after it expires. Everything that uses this login picks up the new token the next time it runs."
                  />
                </>
              )}

              {isAnthropic && (
                <div className="mb-4">
                  <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                    Embeddings Provider Token
                  </label>
                  <SelectComponent
                    control={control}
                    name="credentialData.embeddingServiceTokenId"
                    options={embeddingTokenOptions}
                    isLoading={isEmbeddingTokensLoading}
                    readOnly
                    enableClear={false}
                    formatOptionLabel={option => (
                      <div className="flex items-center gap-2">
                        <img
                          className="w-5 py-2"
                          alt={option.label}
                          src={option.icon}
                        />
                        <span>{option.label}</span>
                      </div>
                    )}
                    placeholder="Select OpenAI or Gemini token"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="edit-integration-token-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="edit-integration-token-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isUpdateIntegrationTokenLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
